/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { FIPEnum } from '../enums/api/fips.enum';
import { FipModel } from '../models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';

/**
 * Network Service
 * Note: This class is to be used to make API calls
 *       and return with a structured response.
 */
@Injectable()
export class FIPApiService {
    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get all floating IPs
     * @returns {Observable<FipModel[]>}
     */
    public getFloatingIPs(options?: any): Observable<FipModel[]> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.get(
                    FIPEnum.GET_ALL_FIPS,
                    moduleAuth
                ).subscribe(
                    (response: FipModel[]): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Add a floating IP
     * @param data
     * @param options
     */
    public addIP(data: FipModel, options?: any): Observable<FipModel> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.post(
                    FIPEnum.POST_FIP(),
                    data,
                    moduleAuth
                ).subscribe(
                    (response: FipModel): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Edit a floating IP
     * @param ipId
     * @param data
     * @param options
     */
    public editIP(ipId: string, data: FipModel, options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.put(
                    FIPEnum.PUT_FIP(ipId),
                    data,
                    moduleAuth
                ).subscribe(
                    (response: FipModel): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Delete a floating IP
     * @param ipId
     * @param options
     * @returns {Observable<any>}
     */
    public deleteFIP(ipId: string, options?: any): Observable<any> {
        return new Observable(
            observer => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.delete(
                    FIPEnum.DELETE_FIP(ipId),
                    moduleAuth
                ).subscribe(
                    (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Enable all/selected floating IPs. If data is empty, then apply to all.
     * @param data
     * @param options
     */
    public enableFloatingIPs(data: any = {}, options?: any): Observable<FipModel[]> {
        return new Observable(
            observer => {
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.put(
                    FIPEnum.ENABLE_FIPS,
                    data,
                    moduleAuth
                ).subscribe(
                    (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Disable all/selected floating IPs. If data is empty, then apply to all.
     * @param data
     * @param options
     */
    public disableFloatingIPs(data: any = {}, options?: any): Observable<FipModel[]> {
        return new Observable(
            observer => {
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.put(
                    FIPEnum.DISABLE_FIPS,
                    data,
                    moduleAuth
                ).subscribe(
                    (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    (error: any): void => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }
}
